const LOCALE = 'es';
const LOCALE_OPTIONS = { sensitivity: 'base' };

export function hasPortalOrder(professional) {
  return (
    typeof professional?.portalOrder === 'number' &&
    Number.isFinite(professional.portalOrder) &&
    professional.portalOrder >= 1
  );
}

export function compareProfessionalsAlphabetically(a, b) {
  const aHasSpeciality = Boolean(a.medicalSpeciality?.trim?.());
  const bHasSpeciality = Boolean(b.medicalSpeciality?.trim?.());

  if (aHasSpeciality !== bHasSpeciality) {
    return aHasSpeciality ? -1 : 1;
  }

  if (aHasSpeciality && bHasSpeciality) {
    const specialityCompare = a.medicalSpeciality.localeCompare(
      b.medicalSpeciality,
      LOCALE,
      LOCALE_OPTIONS,
    );
    if (specialityCompare !== 0) return specialityCompare;
  }

  return (a.name ?? '').localeCompare(b.name ?? '', LOCALE, LOCALE_OPTIONS);
}

/**
 * Ordena médicos para el portal:
 * - portalOrder define posición absoluta
 * - Los huecos se completan con médicos sin portalOrder (orden alfabético: especialidad y luego name)
 * - Sin especialidad van al final
 * - Empates de portalOrder se desempatan igual que el orden automático
 */
export function sortProfessionalsForPortal(professionals) {
  if (!professionals?.length) return [];

  const withOrder = [];
  const withoutOrder = [];

  professionals.forEach((professional) => {
    if (hasPortalOrder(professional)) withOrder.push(professional);
    else withoutOrder.push(professional);
  });

  withoutOrder.sort(compareProfessionalsAlphabetically);

  const orderGroups = new Map();
  withOrder.forEach((professional) => {
    const order = professional.portalOrder;
    if (!orderGroups.has(order)) orderGroups.set(order, []);
    orderGroups.get(order).push(professional);
  });
  orderGroups.forEach((group) => group.sort(compareProfessionalsAlphabetically));

  const total = professionals.length;
  const slots = new Array(total).fill(null);
  const appended = [];
  let unassignedIndex = 0;

  const takeNextUnassigned = () => {
    if (unassignedIndex >= withoutOrder.length) return null;
    return withoutOrder[unassignedIndex++];
  };

  const placeAtOrAfter = (startIndex, professional) => {
    let slot = startIndex;
    while (slot < slots.length && slots[slot] !== null) slot += 1;
    if (slot < slots.length) {
      slots[slot] = professional;
      return slot + 1;
    }
    appended.push(professional);
    return slot + 1;
  };

  for (let position = 1; position <= total; position += 1) {
    const group = orderGroups.get(position);
    if (group?.length) {
      let slot = position - 1;
      for (const professional of group) {
        slot = placeAtOrAfter(slot, professional);
      }
      orderGroups.delete(position);
    }

    const index = position - 1;
    if (index < slots.length && slots[index] === null) {
      const next = takeNextUnassigned();
      if (next) slots[index] = next;
    }
  }

  const placed = new Set([...slots.filter(Boolean), ...appended]);
  const remainingUnassigned = withoutOrder.filter(
    (professional) => !placed.has(professional),
  );
  const remainingOrdered = [...orderGroups.entries()]
    .sort(([a], [b]) => a - b)
    .flatMap(([, group]) => group);

  return [
    ...slots.filter(Boolean),
    ...appended,
    ...remainingUnassigned,
    ...remainingOrdered,
  ];
}
